import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Toast from '../../../components/ui/Toast';
import api from '../../../utils/api';
import { createProduct, deleteProduct, toggleProductStatus, updateProduct } from '../api';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ProductCard from '../components/ProductCard';
import ProductForm from '../components/ProductForm';

const ProductManagementPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchStoreAndData();
  }, [slug]);

  const fetchStoreAndData = async () => {
    try {
      setLoading(true);
      // Fetch the store data using the working endpoint
      const storeResponse = await api.get(`/stores/${slug}`);
      
      if (storeResponse.data.success) {
        const storeData = storeResponse.data.data;
        setStore(storeData);
        // Use the products from the store data
        setProducts(storeData.products || []);
        // Use the categories from the store data
        setCategories(storeData.categories || []);
      } else {
        showToast(storeResponse.data.message || 'Failed to load store', 'error');
      }
    } catch (error) {
      console.error('Error fetching store:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load store';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (formData) => {
    try {
      setIsSubmitting(true);
      const response = await createProduct(slug, formData);
      
      if (response.success) {
        setProducts(prev => [...prev, response.data]);
        setShowForm(false);
        showToast(response.message || 'Product created successfully', 'success');
      } else {
        showToast(response.message || 'Failed to create product', 'error');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create product';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProduct = async (formData) => {
    try {
      setIsSubmitting(true);
      const response = await updateProduct(editingProduct.id, formData);
      
      if (response.success) {
        setProducts(prev => 
          prev.map(prod => 
            prod.id === editingProduct.id ? response.data : prod
          )
        );
        setShowForm(false);
        setEditingProduct(null);
        showToast(response.message || 'Product updated successfully', 'success');
      } else {
        showToast(response.message || 'Failed to update product', 'error');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update product';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!deletingProduct) return;

    try {
      setIsDeleting(true);
      const response = await deleteProduct(deletingProduct.id);
      
      if (response.success) {
        setProducts(prev => prev.filter(prod => prod.id !== deletingProduct.id));
        setShowDeleteModal(false);
        setDeletingProduct(null);
        showToast(response.message || 'Product deleted successfully', 'success');
      } else {
        showToast(response.message || 'Failed to delete product', 'error');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete product';
      showToast(errorMessage, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleProductStatus = async (product) => {
    try {
      setIsToggling(true);
      const response = await toggleProductStatus(product.id);
      
      if (response.success) {
        setProducts(prev => 
          prev.map(prod => 
            prod.id === product.id ? { ...prod, status: prod.status === 'active' ? 'inactive' : 'active' } : prod
          )
        );
        showToast(response.message || 'Product status updated', 'success');
      } else {
        showToast(response.message || 'Failed to update product status', 'error');
      }
    } catch (error) {
      console.error('Error toggling product status:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update product status';
      showToast(errorMessage, 'error');
    } finally {
      setIsToggling(false);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDeleteClick = (product) => {
    setDeletingProduct(product);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = (formData) => {
    if (editingProduct) {
      return handleUpdateProduct(formData);
    } else {
      return handleCreateProduct(formData);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  // Filter products based on search term and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.details && product.details.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !selectedCategory || product.category_id?.toString() === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Store Not Found</h2>
          <p className="text-gray-600 mb-6">The store you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/admin/stores')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Stores
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Manage Products: {store.name}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Create, edit, and delete products for your store
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <span className="mr-2">+</span>
            Add Product
          </button>
          <button
            onClick={() => navigate('/admin/stores')}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
          >
            ← Back to Stores
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400">🔍</span>
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || selectedCategory ? 'No products found' : 'No products yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedCategory 
              ? 'Try adjusting your search terms or category filter.' 
              : 'Get started by creating your first product.'
            }
          </p>
          {!searchTerm && !selectedCategory && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <span className="mr-2">+</span>
              Create First Product
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              isAdmin={true}
              onEdit={handleEditProduct}
              onDelete={handleDeleteClick}
              onToggleStatus={handleToggleProductStatus}
              store={store}
            />
          ))}
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <ProductForm
              product={editingProduct}
              categories={categories}
              store={store}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingProduct(null);
        }}
        onConfirm={handleDeleteProduct}
        productName={deletingProduct?.name}
        isDeleting={isDeleting}
      />

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default ProductManagementPage; 